/**
 * CollabReadView — read-mode HTML render for collab-enabled posts.
 *
 * Push-only path (2026-05-29): opens a passive Yjs WebSocket against
 * `/api/v1/ws/posts/<id>` (same endpoint the Editor uses, but without
 * y-tiptap binding), keeps a local Y.Doc mirror, and re-derives the
 * materialized text + structured paragraph list whenever Yjs notifies
 * us of a change. No HTTP polling.
 *
 * Previously this polled `/crdt/text` + `/crdt/structured` every 8 s.
 *
 * Author-Highlight Toggle (V2.7.3, 2026-05-20): an opt-in button shows
 * a colored left-border + tooltip per paragraph attributing each block
 * to its writer. Off by default — clean markdown view stays default.
 * Choice persisted in localStorage.
 */

import { useEffect, useState } from 'react'
import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'
import { CONTENT_WS } from '../config'
import { getAccessToken } from '../auth'
import type { Post } from '../types'
import { AnnotationTooltip } from './AnnotationTooltip'
import { useMermaidAsync } from '../hooks/useMermaidAsync'
import { useD2Async } from '../hooks/useD2Async'
import { InlineSectionRenderer } from './InlineSectionRenderer'
import { annotationsFromSections } from './annotationsFromSections'
import { ReadModeAnnotationOverlay } from './ReadModeAnnotationOverlay'

interface Props {
  post: Post
  renderHtml: (markdown: string) => string
  /** Legacy prop — ignored, kept for callsite-compat. The view is
   *  push-driven now (Yjs WebSocket), no polling interval needed. */
  pollMs?: number
  /** External toggle from the meta-row "Autoren" chip. When undefined,
   *  CollabReadView falls back to its own localStorage-managed state.
   *  Plumbing this from the parent lets PostEditor host the toggle UI
   *  in the meta-row alongside Author / Type / Notify chips. */
  showAuthors?: boolean
  /** Optional handler — clicking the "Antworten"-Button on a
   *  Question-section card calls this with the section_id. Parent
   *  (PostEditor) typically opens a SectionReplyModal in response. */
  onReplyToSection?: (sectionId: string) => void
  /** P1.4: viewing user's display name — passed through to the
   *  section renderer so it can decide Lead/CC visuals. */
  currentUserName?: string
}

interface StructuredParagraph {
  index: number
  text: string
  author_id?: string | null
  author_name?: string | null
  author_color?: string | null
  is_heading?: boolean
  heading_level?: number | null
  block_id?: string | null
  // commit_id is the shared UUID the server stamps on every paragraph
  // produced by a single REST doc_apply_text call. Frontend uses it
  // to draw one continuous author-stripe across the whole commit
  // instead of one stripe per paragraph (Alex 2026-05-30: stripes
  // pro Paragraph sind verwirrend, ein Push = eine Linie).
  commit_id?: string | null
  created_at?: string | null
  section_id?: string | null
  section_type?: 'product' | 'question' | 'task' | 'annotation' | null
  section_role?: 'start' | 'content' | 'reply' | 'end' | null
  section_attrs?: Record<string, unknown> | null
}

const HIGHLIGHT_STORAGE_KEY = 'collab_show_authors'

/**
 * Materialize a Yjs XmlFragment into the same shape `/crdt/text` and
 * `/crdt/structured` return, so the existing render-paths just work.
 * Mirrors the server's logic in routes/crdt_apply.py (strip tags, join
 * paragraphs with \n).
 */
function materializeFromYjs(
  yfrag: Y.XmlFragment,
): { text: string; paragraphs: StructuredParagraph[] } {
  const paragraphs: StructuredParagraph[] = []
  const lines: string[] = []
  let i = 0
  // Recursive text extraction: walks XmlText children and concatenates
  // their values verbatim. Same approach as the server's
  // extract_text_content helper. Previous impl regex-stripped
  // `<[^>]+>` which annihilated user-typed XML inside ```xml```
  // codeblocks (Alex 2026-05-30 Post 825: <gps_update> tags vanished).
  const extractText = (node: Y.XmlElement | Y.XmlText): string => {
    if (node instanceof Y.XmlText) return node.toString()
    const out: string[] = []
    for (const c of node.toArray()) {
      if (c instanceof Y.XmlText || c instanceof Y.XmlElement) {
        out.push(extractText(c))
      }
    }
    return out.join('')
  }

  yfrag.forEach((child) => {
    if (!(child instanceof Y.XmlElement)) return
    const attrs = child.getAttributes() as Record<string, string | undefined>
    const text = extractText(child)
    lines.push(text)
    let sectionAttrs: Record<string, unknown> | null = null
    if (typeof attrs.section_attrs === 'string' && attrs.section_attrs) {
      try {
        const parsed = JSON.parse(attrs.section_attrs)
        if (parsed && typeof parsed === 'object') {
          sectionAttrs = parsed as Record<string, unknown>
        }
      } catch {
        sectionAttrs = null
      }
    }
    const isHeadingByName =
      child.nodeName === 'heading' || child.nodeName === 'Heading'
    const headingMatch = text.match(/^(#{1,3})\s+\S/)
    const isHeading = isHeadingByName || !!headingMatch
    const headingLevel = headingMatch
      ? headingMatch[1].length
      : attrs.level
        ? parseInt(attrs.level, 10) || null
        : null
    paragraphs.push({
      index: i++,
      text,
      author_id: attrs.author_id ?? null,
      author_name: attrs.author_name ?? null,
      author_color: attrs.author_color ?? null,
      is_heading: isHeading,
      heading_level: headingLevel,
      block_id: attrs.block_id ?? null,
      commit_id: attrs.commit_id ?? null,
      created_at: attrs.created_at ?? null,
      section_id: attrs.section_id ?? null,
      section_type:
        (attrs.section_type as
          | 'product'
          | 'question'
          | 'task'
          | 'annotation'
          | undefined) ?? null,
      section_role:
        (attrs.section_role as
          | 'start'
          | 'content'
          | 'reply'
          | 'end'
          | undefined) ?? null,
      section_attrs: sectionAttrs,
    })
  })
  return { text: lines.join('\n'), paragraphs }
}

export function CollabReadView({
  post,
  renderHtml,
  showAuthors: externalShowAuthors,
  onReplyToSection,
  currentUserName,
}: Props) {
  const [text, setText] = useState<string | null>(null)
  const [structured, setStructured] = useState<StructuredParagraph[] | null>(null)
  // Internal fallback (only used when no external prop provided).
  const [internalShowAuthors] = useState<boolean>(() => {
    try {
      return localStorage.getItem(HIGHLIGHT_STORAGE_KEY) === '1'
    } catch {
      return false
    }
  })
  const showAuthors = externalShowAuthors !== undefined ? externalShowAuthors : internalShowAuthors

  useEffect(() => {
    const ydoc = new Y.Doc()
    const token = getAccessToken()
    const baseUrl = `${CONTENT_WS}/api/v1/ws/posts/${post.id}`
    const params: Record<string, string> = {}
    if (token) params.token = token
    const provider = new WebsocketProvider(baseUrl, 'crdt', ydoc, {
      connect: true,
      params,
      protocols: token ? [`bearer.${token}`] : [],
    } as unknown as ConstructorParameters<typeof WebsocketProvider>[3])

    const yfrag = ydoc.get('content', Y.XmlFragment as unknown as new () => Y.XmlFragment) as Y.XmlFragment

    const recompute = () => {
      const result = materializeFromYjs(yfrag)
      setText((prev) => (prev === result.text ? prev : result.text))
      setStructured((prev) => {
        if (!prev || prev.length !== result.paragraphs.length) return result.paragraphs
        for (let i = 0; i < prev.length; i++) {
          const a = prev[i]
          const b = result.paragraphs[i]
          if (
            a.text !== b.text ||
            a.author_id !== b.author_id ||
            a.section_id !== b.section_id ||
            a.section_role !== b.section_role ||
            a.block_id !== b.block_id
          ) {
            return result.paragraphs
          }
        }
        return prev
      })
    }
    yfrag.observeDeep(recompute)
    // First materialize after the initial sync round-trip. y-websocket
    // emits 'sync' once the server-state is applied.
    provider.on('sync', recompute)
    // Best-effort initial recompute too — yfrag may already have
    // content if the editor was open on another tab + Yjs persisted.
    recompute()

    return () => {
      yfrag.unobserveDeep(recompute)
      provider.destroy()
      ydoc.destroy()
    }
    // post.id is the only intentional dependency — token is read fresh
    // at provider-construct time, and showAuthors does not affect the
    // subscription itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id])

  // Issue #15 follow-up: ref-as-state so AnnotationTooltip can attach
  // delegated hover-listeners on the read-mode markdown container.
  // Also drives `useMermaidAsync` which renders erDiagram /
  // sequenceDiagram / classDiagram placeholders that beautiful-mermaid
  // can't handle (Alex 2026-05-27 — empty entity boxes on Post #814).
  const [readModeRoot, setReadModeRoot] = useState<HTMLElement | null>(null)
  useMermaidAsync(readModeRoot)
  useD2Async(readModeRoot)

  // Two-step grouping for the authors-on view:
  //  1. Atoms: a fenced code-block (mermaid/d2/etc.) spans multiple
  //     paragraphs because of the \n\n chunk-split. We bundle all the
  //     paragraphs between matching ``` fences so marked() sees the
  //     whole block — otherwise diagrams render as raw fragments.
  //  2. Commit-groups: atoms that share (author_id, commit_id) become
  //     a single visual block with ONE continuous left-border.
  //     commit_id is the server-stamped UUID shared across every
  //     paragraph created by a single doc_apply_text call. Without
  //     this grouping the user sees N stripes for one "send" — Alex
  //     2026-05-30: "da kommt ja auf einmal alles gepusht, ich will
  //     EINE linie pro push, nicht pro paragraph".
  // (The legacy buildAtoms / buildCommitGroups / renderAtomParagraph /
  // renderHighlighted helpers were removed alongside the highlight-mode
  // render — InlineSectionRenderer is now the single read-view path
  // and computes its own commit grouping internally. Removed in the
  // follow-up that turns `npm run build` green from clean ecc9601.)

  // Fall back to snapshot content while the first fetch is in flight.
  const md = text !== null ? text : post.content || ''

  return (
    <>
      {structured ? (
        <div ref={(el) => setReadModeRoot(el)} className="editor-content">
          <InlineSectionRenderer
            paragraphs={structured}
            renderMarkdown={renderHtml}
            onReply={onReplyToSection}
            postId={post.id}
            notifyAuthors={
              ((post.metadata_json as Record<string, unknown> | undefined)
                ?.notify_authors as string[] | undefined) || []
            }
            showAuthors={showAuthors}
            currentUserName={currentUserName}
            onSectionCreated={() => {
              /* Yjs WS pushes the new section into the local Y.Doc —
                 our materializeFromYjs effect re-runs automatically,
                 so no manual refetch is needed. */
            }}
          />
        </div>
      ) : (
        <div
          ref={(el) => setReadModeRoot(el)}
          className="editor-content"
          dangerouslySetInnerHTML={{ __html: renderHtml(md) }}
        />
      )}
      <AnnotationTooltip
        editorRoot={readModeRoot}
        annotations={[
          ...((post.annotations || []) as any),
          ...(structured ? annotationsFromSections(structured) : []),
        ]}
      />
      {/* Read-Mode "Anmerken"-Selektion (2026-06-12). Native window
          selection → resolveAnchor() findet das passende paragraph per
          substring-match → POST als annotation-Section (drift-stable). */}
      <ReadModeAnnotationOverlay
        postId={post.id}
        paragraphs={structured}
        readModeRoot={readModeRoot}
      />
    </>
  )
}
